import { HelpCircle } from "lucide-react";
import { useDisponibilidadeCounts } from "@/hooks/useDisponibilidade";
import {
  getDisponibilidadeConfig,
  DISPONIBILIDADE_BUCKETS,
  TEMPERATURA_CONFIG,
  TEMPERATURAS,
  EIXOS_FILTRO,
  axisValueLabel,
  axisValueDescricao,
  axisValueBadgeClass,
  eixosCompostosPorSelecao,
  efetivasEquivalentesASelecao,
  type DisponibilidadeEfetiva,
  type Temperatura,
  type SelecaoEixos,
  type DisponibilidadeBucket,
} from "@/utils/disponibilidadeUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DisponibilidadeCountChipsProps {
  /** Valores de disponibilidade_efetiva atualmente filtrados (filters.disponibilidade). */
  activeEfetiva?: string[];
  /** Alterna o filtro pelos valores do bucket clicado (ADITIVO: soma/remove do conjunto). */
  onToggleEfetiva?: (values: DisponibilidadeEfetiva[]) => void;
  /** Seleção atual por eixo (filters.disp_emprego / disp_interesse / disp_relacao). */
  activeEixos?: SelecaoEixos;
  onToggleEixo?: (eixoKey: keyof SelecaoEixos, value: string) => void;
  /** Seleção atual de temperatura (filters.disp_temperatura). */
  activeTemperatura?: string[];
  onToggleTemperatura?: (value: Temperatura) => void;
}

const ORDEM_TEMPERATURA: Temperatura[] = ["quente", "morno", "frio"];

// Labels curtos pra caber os 3 eixos numa linha; o nome completo vai no title.
const LABEL_CURTO_EIXO: Record<string, string> = {
  disp_interesse: "Interesse",
  disp_emprego: "Emprego",
  disp_relacao: "Relação",
};
const COR_ESPECTRO: Record<string, string> = {
  quente: "bg-orange-400",
  morno: "bg-amber-300",
  frio: "bg-sky-300",
  sem_info: "bg-slate-200",
};

/** Micro barra + texto com o espectro de temperatura de um grupo (tooltip). */
function EspectroTemperatura({ porTemperatura }: { porTemperatura: Record<string, number> }) {
  const total = Object.values(porTemperatura).reduce((soma, valor) => soma + valor, 0);
  if (!total) return null;
  const partes = [...ORDEM_TEMPERATURA, "sem_info" as Temperatura].filter(
    (temperatura) => (porTemperatura[temperatura] ?? 0) > 0,
  );
  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        {partes.map((temperatura) => (
          <div
            key={temperatura}
            className={COR_ESPECTRO[temperatura]}
            style={{ width: `${((porTemperatura[temperatura] ?? 0) / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground">
        {partes
          .map((temperatura) => `${porTemperatura[temperatura]} ${TEMPERATURA_CONFIG[temperatura].text.toLowerCase()}`)
          .join(" · ")}
      </div>
    </div>
  );
}

function GlossarioPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-muted-foreground hover:text-foreground"
          aria-label="Como funciona a disponibilidade?"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 text-xs leading-relaxed">
        <p className="font-semibold text-sm mb-2">Como funciona a disponibilidade?</p>
        <ul className="list-disc pl-4 space-y-1.5">
          <li>
            Cada freelancer tem três informações separadas: <b>situação de emprego</b>, <b>interesse em freela</b> e{" "}
            <b>relação com a gente</b>. O status (Disponível, Vale abordar...) é derivado da combinação delas com a
            agenda de projetos.
          </li>
          <li>
            A <b>temperatura</b> (Quente, Morno, Frio) considera também o tempo: uma resposta positiva antiga esfria,
            um projeto recém-terminado esquenta. "Sem info" significa que não temos dado para estimar.
          </li>
          <li>Quem não respondeu nada aparece como "Sem info". Não inventamos disponibilidade.</li>
          <li>
            Todo chip é um filtro: clique para somar à seleção, clique de novo para remover. Os números são da base
            inteira; o total filtrado aparece na listagem.
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export function DisponibilidadeCountChips({
  activeEfetiva,
  onToggleEfetiva,
  activeEixos,
  onToggleEixo,
  activeTemperatura,
  onToggleTemperatura,
}: DisponibilidadeCountChipsProps) {
  const { data: counts, isLoading } = useDisponibilidadeCounts();
  if (isLoading || !counts) return null;

  const efetivaAtiva = activeEfetiva ?? [];
  const eixosAtivos = activeEixos ?? {};
  const temperaturaAtiva = activeTemperatura ?? [];

  // Ecos indicativos (nunca filtram): agregado selecionado -> destaca itens de eixo que o
  // compõem; combinação de eixos selecionada -> destaca o agregado equivalente.
  const ecoEixos = eixosCompostosPorSelecao(efetivaAtiva);
  const ecoEfetivas = efetivasEquivalentesASelecao(eixosAtivos);

  const espectroDoBucket = (bucket: DisponibilidadeBucket): Record<string, number> => {
    const espectro: Record<string, number> = {};
    for (const value of bucket.values) {
      for (const [temperatura, quantidade] of Object.entries(counts.porEfetivaTemperatura[value] ?? {})) {
        espectro[temperatura] = (espectro[temperatura] ?? 0) + quantidade;
      }
    }
    return espectro;
  };

  const chipBase =
    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium cursor-pointer transition hover:brightness-95";

  const visibleBuckets = DISPONIBILIDADE_BUCKETS.map((bucket) => ({
    ...bucket,
    count: bucket.values.reduce((total, value) => total + (counts.porEfetiva[value] ?? 0), 0),
    isActive: bucket.values.every((value) => efetivaAtiva.includes(value)),
    isEco: !bucket.values.every((value) => efetivaAtiva.includes(value)) && bucket.values.some((value) => ecoEfetivas.includes(value)),
  })).filter((bucket) => bucket.count > 0);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-1.5">
        {/* Temperatura + status agregados numa linha só; o glossário explica o conjunto.
            Label alinhado com os eixos abaixo; bold pra marcar a linha principal. */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="flex w-20 shrink-0 items-center gap-1 text-[11px] font-semibold text-muted-foreground">
            Status: <GlossarioPopover />
          </span>
          {TEMPERATURAS.map((temperatura) => {
            const config = TEMPERATURA_CONFIG[temperatura];
            const count = counts.porTemperatura[temperatura] ?? 0;
            if (!count) return null;
            const Icon = config.icon;
            const isActive = temperaturaAtiva.includes(temperatura);
            return (
              <Tooltip key={temperatura}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onToggleTemperatura?.(temperatura)}
                    aria-pressed={isActive}
                    className={cn(chipBase, config.bg, config.color, config.border, isActive && "ring-2 ring-offset-1 ring-current")}
                  >
                    <Icon className="h-3 w-3 shrink-0" />
                    {config.text}
                    <span className="font-semibold tabular-nums">{count}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">{config.descricao}</TooltipContent>
              </Tooltip>
            );
          })}

          <span aria-hidden className="mx-0.5 h-4 w-px shrink-0 bg-border" />

          {/* Status agregados (multi-select aditivo). */}
          {visibleBuckets.map((bucket) => {
            const config = getDisponibilidadeConfig(bucket.representativeValue);
            const Icon = config.icon;
            return (
              <Tooltip key={bucket.label}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => onToggleEfetiva?.(bucket.values)}
                    aria-pressed={bucket.isActive}
                    className={cn(
                      chipBase,
                      config.bg,
                      config.color,
                      config.border,
                      bucket.isActive && "ring-2 ring-offset-1 ring-current",
                      bucket.isEco && "outline-dashed outline-2 outline-offset-1 outline-current opacity-90",
                    )}
                  >
                    <Icon className="h-3 w-3 shrink-0" />
                    {bucket.label}
                    <span className="font-semibold tabular-nums">{bucket.count}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  <p>{bucket.descricao}</p>
                  {bucket.isEco && <p className="mt-1 italic">Equivale à combinação selecionada abaixo.</p>}
                  <EspectroTemperatura porTemperatura={espectroDoBucket(bucket)} />
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Itens de cada eixo, uma linha por eixo: filtro individual, sem passar
            pelo agrupamento. Label curto; o nome completo fica no title. */}
        {EIXOS_FILTRO.map((eixo) => {
          const selecionados = eixosAtivos[eixo.key] ?? [];
          const eco = ecoEixos[eixo.key] ?? [];
          const itens = eixo.values.filter((value) => (counts.porEixo[eixo.key][value] ?? 0) > 0);
          if (!itens.length) return null;
          return (
            <div key={eixo.key} className="flex flex-wrap items-center gap-1.5">
              <span
                className="w-20 shrink-0 text-[11px] text-muted-foreground"
                title={eixo.label}
              >
                {LABEL_CURTO_EIXO[eixo.key] ?? eixo.label}:
              </span>
              {itens.map((value) => {
                const isActive = selecionados.includes(value);
                const isEco = !isActive && eco.includes(value);
                return (
                  <Tooltip key={value}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => onToggleEixo?.(eixo.key, value)}
                        aria-pressed={isActive}
                        className={cn(
                          chipBase,
                          "border",
                          axisValueBadgeClass(value),
                          isActive && "ring-2 ring-offset-1 ring-current",
                          isEco && "outline-dashed outline-2 outline-offset-1 outline-current opacity-90",
                        )}
                      >
                        {axisValueLabel(eixo.dimension, value)}
                        <span className="font-semibold tabular-nums">{counts.porEixo[eixo.key][value]}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      <p>{axisValueDescricao(value)}</p>
                      {isEco && <p className="mt-1 italic">Faz parte da composição do status selecionado acima.</p>}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

export default DisponibilidadeCountChips;
